import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { api, money, errMsg } from "@/lib/api";
import { Container, InlineLoader } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Checkbox } from "@/components/ui/checkbox";
import { Heart, Lock, Loader2, AlertCircle, ShieldCheck, Repeat, CalendarClock, Users, CreditCard, Bitcoin, HeartHandshake, Zap } from "lucide-react";
import { Brand } from "@/components/Brand";
import EmbeddedCheckoutModal from "@/components/EmbeddedCheckoutModal";

const PRESETS = [25, 50, 100, 250];
const TIP_OPTIONS = [0, 5, 10, 15];
const VISIBILITY = [
  { v: "name_amount", label: "Show my name and amount" },
  { v: "name_only", label: "Show my name only" },
  { v: "anonymous", label: "Donate anonymously" },
  { v: "hidden", label: "Hide me from the public supporter list" },
];

export default function DonationCheckout() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [c, setC] = useState(null);
  const [amount, setAmount] = useState(50);
  const [tipPct, setTipPct] = useState(10);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [smsOptIn, setSmsOptIn] = useState(false);
  const [message, setMessage] = useState("");
  const [visibility, setVisibility] = useState("name_amount");
  const [donationType, setDonationType] = useState("one_time");
  const [paymentMethod, setPaymentMethod] = useState("card");
  const [memberId, setMemberId] = useState(searchParams.get("member") || "");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [cryptoEnabled, setCryptoEnabled] = useState(true);
  const [stripePk, setStripePk] = useState("");
  const [clientSecret, setClientSecret] = useState("");

  useEffect(() => {
    api.get(`/campaigns/${slug}`).then((r) => setC(r.data)).catch((e) => setError(errMsg(e)));
    api.get("/settings/public").then((r) => {
      setCryptoEnabled(r.data.crypto_enabled !== false);
      if (typeof r.data.default_tip_pct === "number") setTipPct(r.data.default_tip_pct);
      if (r.data.stripe_publishable_key) setStripePk(r.data.stripe_publishable_key);
    }).catch(() => {});
  }, [slug]);

  const recurring = donationType === "recurring";
  const tipAmount = recurring ? 0 : Math.round((amount * tipPct) / 100 * 100) / 100;
  const total = Math.round((Number(amount) + tipAmount) * 100) / 100;
  const isCrypto = !recurring && cryptoEnabled && paymentMethod === "crypto";

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!amount || amount < 1) return setError("Please enter a donation amount of at least $1.");
    setSubmitting(true);
    try {
      const { data } = await api.post("/donations/create-intent", {
        campaign_id: c.id,
        amount: Number(amount),
        tip_amount: tipAmount,
        donor_name: visibility === "anonymous" || visibility === "hidden" ? "Anonymous" : name || "Anonymous",
        donor_email: email,
        visibility,
        message,
        origin_url: window.location.origin,
        donation_type: donationType,
        recurring_interval: recurring ? "month" : null,
        team_member_id: memberId || null,
        payment_method: recurring ? "card" : paymentMethod,
        donor_phone: phone.trim() || null,
        sms_opt_in: smsOptIn,
        source_ref: (() => { try { return sessionStorage.getItem("ff_ref") || ""; } catch (_) { return ""; } })(),
      });
      setClientSecret(data.client_secret); // opens embedded Stripe checkout in a modal
    } catch (err) {
      setError(errMsg(err));
      setSubmitting(false);
    }
  };

  if (error && !c) return <Container className="py-20"><Alert variant="destructive"><AlertCircle className="h-4 w-4" /><AlertDescription>{error}</AlertDescription></Alert></Container>;
  if (!c) return <InlineLoader label="Loading" />;

  const feePct = c.organizer_pro ? 3 : 5;
  const organizerReceives = Math.round((Number(amount) || 0) * (100 - feePct)) / 100;

  return (
    <div className="bg-[#F3F4F6]">
      <Container className="grid gap-8 py-10 lg:grid-cols-12">
        <form id="donation-form" onSubmit={submit} className="lg:col-span-7" data-testid="donation-form">
          <h1 className="font-heading text-2xl font-extrabold tracking-tight text-ff-blue sm:text-3xl">
            Support “{c.title}”
          </h1>
          <p className="mt-2 text-sm text-ff-dark">Donate in seconds — no account required.</p>

          {error && (
            <Alert variant="destructive" className="mt-5" data-testid="donation-error">
              <AlertCircle className="h-4 w-4" /><AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Frequency */}
          <div className="mt-6 rounded-2xl border border-ff-border bg-white p-6">
            <Label className="text-ff-blue">Donation frequency</Label>
            <div className="mt-3 grid grid-cols-2 gap-2">
              <button type="button" data-testid="freq-one-time" onClick={() => setDonationType("one_time")}
                className={`flex items-center justify-center gap-2 rounded-lg border py-3 text-sm font-bold transition-colors duration-200 ${!recurring ? "border-ff-blue bg-ff-blue text-white" : "border-ff-border text-ff-blue hover:border-ff-blue"}`}>
                <Heart className="h-4 w-4" /> One-time
              </button>
              <button type="button" data-testid="freq-monthly" onClick={() => { setDonationType("recurring"); setPaymentMethod("card"); }}
                className={`flex items-center justify-center gap-2 rounded-lg border py-3 text-sm font-bold transition-colors duration-200 ${recurring ? "border-ff-blue bg-ff-blue text-white" : "border-ff-border text-ff-blue hover:border-ff-blue"}`}>
                <Repeat className="h-4 w-4" /> Monthly
              </button>
            </div>
            {recurring && (
              <div className="mt-3 flex items-center gap-2 rounded-lg bg-[#F3F4F6] p-3 text-xs text-ff-dark" data-testid="recurring-note">
                <CalendarClock className="h-4 w-4 text-ff-blue" /> You'll be charged this amount every month. Cancel anytime.
              </div>
            )}
          </div>

          {/* Team member (team campaigns) */}
          {c.is_team && c.team_members?.length > 0 && (
            <div className="mt-4 rounded-2xl border border-ff-border bg-white p-6" data-testid="team-member-select-card">
              <Label className="flex items-center gap-2 text-ff-blue"><Users className="h-4 w-4" /> Support a team member (optional)</Label>
              <Select value={memberId} onValueChange={setMemberId}>
                <SelectTrigger className="mt-3" data-testid="team-member-select"><SelectValue placeholder="The whole team" /></SelectTrigger>
                <SelectContent>
                  {c.team_members.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Amount */}
          <div className="mt-4 rounded-2xl border border-ff-border bg-white p-6">
            <Label className="text-ff-blue">Your donation</Label>
            {Array.isArray(c?.giving_tiers) && c.giving_tiers.length > 0 && (
              <div className="mt-3 space-y-2" data-testid="giving-tiers">
                {c.giving_tiers.map((t, i) => (
                  <button type="button" key={i} data-testid={`giving-tier-${i}`} onClick={() => setAmount(Number(t.amount))}
                    className={`flex w-full items-center justify-between gap-3 rounded-xl border p-3 text-left transition-colors duration-200 ${Number(amount) === Number(t.amount) ? "border-ff-blue bg-[#EFF4FB]" : "border-ff-border hover:border-ff-blue"}`}>
                    <div>
                      <div className="text-sm font-bold text-ff-blue">{money(Number(t.amount))}{t.label ? ` · ${t.label}` : ""}</div>
                      {t.description && <div className="text-xs text-ff-medium">{t.description}</div>}
                    </div>
                    <span className={`flex h-5 w-5 items-center justify-center rounded-full border-2 ${Number(amount) === Number(t.amount) ? "border-ff-blue bg-ff-blue" : "border-ff-border"}`}>
                      {Number(amount) === Number(t.amount) && <span className="h-2 w-2 rounded-full bg-white" />}
                    </span>
                  </button>
                ))}
              </div>
            )}
            <div className="mt-3 grid grid-cols-4 gap-2">
              {PRESETS.map((p) => (
                <button type="button" key={p} data-testid={`amount-preset-${p}`} onClick={() => setAmount(p)}
                  className={`rounded-lg border py-3 text-sm font-bold transition-colors duration-200 ${Number(amount) === p ? "border-ff-blue bg-ff-blue text-white" : "border-ff-border text-ff-blue hover:border-ff-blue"}`}>
                  ${p}
                </button>
              ))}
            </div>
            <div className="relative mt-3">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 font-bold text-ff-medium">$</span>
              <Input type="number" min="1" step="1" data-testid="amount-input" value={amount}
                onChange={(e) => setAmount(e.target.value)} className="pl-7 text-lg font-bold" />
            </div>
          </div>

          {/* Tip */}
          {!recurring && (
          <div className="mt-4 rounded-2xl border border-ff-border bg-white p-6">
            <Label className="text-ff-blue">Add an optional tip to <Brand /></Label>
            <p className="mt-1 text-xs text-ff-medium">
              Tips to <Brand /> are optional and help keep us running. Separately, funds raised are subject to a 5% platform fee (3% for Pro organizers) + Stripe processing fee.
            </p>
            <div className="mt-3 grid grid-cols-4 gap-2">
              {TIP_OPTIONS.map((t) => (
                <button type="button" key={t} data-testid={`tip-${t}`} onClick={() => setTipPct(t)}
                  className={`rounded-lg border py-2.5 text-sm font-bold transition-colors duration-200 ${tipPct === t ? "border-ff-blue bg-ff-blue text-white" : "border-ff-border text-ff-blue hover:border-ff-blue"}`}>
                  {t}%
                </button>
              ))}
            </div>
          </div>
          )}

          {/* Payment method (one-time only; crypto not supported for monthly) */}
          {!recurring && cryptoEnabled && (
          <div className="mt-4 rounded-2xl border border-ff-border bg-white p-6" data-testid="payment-method-card">
            <Label className="text-ff-blue">Payment method</Label>
            <div className="mt-3 grid grid-cols-2 gap-2">
              <button type="button" data-testid="pay-method-card" onClick={() => setPaymentMethod("card")}
                className={`flex items-center justify-center gap-2 rounded-lg border py-3 text-sm font-bold transition-colors duration-200 ${paymentMethod === "card" ? "border-ff-blue bg-ff-blue text-white" : "border-ff-border text-ff-blue hover:border-ff-blue"}`}>
                <CreditCard className="h-4 w-4" /> Card
              </button>
              <button type="button" data-testid="pay-method-crypto" onClick={() => setPaymentMethod("crypto")}
                className={`flex items-center justify-center gap-2 rounded-lg border py-3 text-sm font-bold transition-colors duration-200 ${paymentMethod === "crypto" ? "border-ff-blue bg-ff-blue text-white" : "border-ff-border text-ff-blue hover:border-ff-blue"}`}>
                <Bitcoin className="h-4 w-4" /> Crypto
              </button>
            </div>
            {isCrypto && (
              <div className="mt-3 flex items-center gap-2 rounded-lg bg-[#F3F4F6] p-3 text-xs text-ff-dark" data-testid="crypto-note">
                <Bitcoin className="h-4 w-4 shrink-0 text-ff-blue" /> Pay with cryptocurrency or stablecoins. You'll be charged {money(total)} in USD; FightFund receives U.S. dollars.
              </div>
            )}
          </div>
          )}

          {/* Details */}
          <div className="mt-4 space-y-4 rounded-2xl border border-ff-border bg-white p-6">
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label htmlFor="d-name">Your name</Label>
                <Input id="d-name" data-testid="donor-name-input" value={name}
                  onChange={(e) => setName(e.target.value)} className="mt-1.5" placeholder="Jane Donor" />
              </div>
              <div>
                <Label htmlFor="d-email">Email (for receipt)</Label>
                <Input id="d-email" type="email" required data-testid="donor-email-input" value={email}
                  onChange={(e) => setEmail(e.target.value)} className="mt-1.5" placeholder="you@example.com" />
              </div>
            </div>
            <div>
              <Label htmlFor="d-msg">Message of support (optional)</Label>
              <Textarea id="d-msg" data-testid="donor-message-input" rows={2} value={message}
                onChange={(e) => setMessage(e.target.value)} className="mt-1.5" />
            </div>
            <div>
              <Label htmlFor="d-phone">Mobile number <span className="font-normal text-ff-medium">(optional)</span></Label>
              <Input id="d-phone" type="tel" data-testid="donor-phone-input" value={phone}
                onChange={(e) => setPhone(e.target.value)} className="mt-1.5" placeholder="+1 (555) 123-4567" />
              <label className="mt-2.5 flex cursor-pointer items-start gap-2.5" data-testid="donor-sms-optin-label">
                <Checkbox checked={smsOptIn} onCheckedChange={(v) => setSmsOptIn(!!v)}
                  data-testid="donor-sms-optin" className="mt-0.5" />
                <span className="text-xs leading-relaxed text-ff-medium">
                  Text me a receipt and updates about this campaign. Optional — you'll still get an email receipt. Msg &amp; data rates may apply; reply STOP to opt out.
                </span>
              </label>
            </div>
            <div>
              <Label className="text-ff-blue">Public visibility</Label>
              <RadioGroup value={visibility} onValueChange={setVisibility} className="mt-2 space-y-1">
                {VISIBILITY.map((o) => (
                  <label key={o.v} data-testid={`visibility-${o.v}`}
                    className="flex cursor-pointer items-center gap-3 rounded-lg border border-ff-border p-3 text-sm hover:border-ff-blue">
                    <RadioGroupItem value={o.v} /> <span className="text-ff-dark">{o.label}</span>
                  </label>
                ))}
              </RadioGroup>
            </div>
          </div>
        </form>

        {/* Summary */}
        <aside className="lg:col-span-5">
          <div className="sticky top-24 rounded-2xl border border-ff-border bg-white p-6 shadow-sm">
            <h3 className="font-heading text-lg font-bold text-ff-blue">Donation summary</h3>
            <div className="mt-4 space-y-2 text-sm">
              <Row label={recurring ? "Monthly donation" : "Donation"} value={money(Number(amount) || 0)} />
              {!recurring && <Row label={`FightFund tip (${tipPct}%)`} value={money(tipAmount)} />}
              <div className="my-2 border-t border-ff-border" />
              <Row label={recurring ? "Charged monthly" : "Total charged today"} value={money(total)} bold />
            </div>
            <div className="mt-3 flex items-center justify-between rounded-lg bg-[#ECFDF5] px-3 py-2.5" data-testid="organizer-receives-row">
              <span className="flex items-center gap-1.5 text-sm font-semibold text-ff-green">
                <HeartHandshake className="h-4 w-4" /> Organizer receives
              </span>
              <span className="font-heading text-base font-extrabold text-ff-green" data-testid="organizer-receives-amount">
                ≈ {money(organizerReceives)}{recurring ? " / mo" : ""}
              </span>
            </div>
            <p className="mt-1.5 text-center text-[11px] text-ff-medium" data-testid="organizer-receives-note">
              After the {feePct}% platform fee{tipAmount > 0 ? " (your tip is separate)" : ""} · before Stripe processing
            </p>
            <Button type="submit" form="donation-form" disabled={submitting} data-testid="donate-submit-button"
              className="mt-5 w-full bg-ff-action text-base font-semibold text-white shadow-md hover:bg-ff-actionDark">
              {submitting ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Opening secure checkout</> : isCrypto ? <><Bitcoin className="mr-2 h-4 w-4" /> Pay {money(total)} with crypto</> : <><Heart className="mr-2 h-4 w-4" /> Donate {money(total)}{recurring ? " / mo" : ""}</>}
            </Button>
            {!isCrypto && (
              <div className="mt-2.5 flex items-center justify-center gap-1.5 text-xs font-semibold text-ff-green" data-testid="express-wallet-note">
                <Zap className="h-3.5 w-3.5 fill-current" /> One-tap giving with Apple Pay &amp; Google Pay
              </div>
            )}
            <div className="mt-4 flex items-center justify-center gap-1.5 text-xs text-ff-medium">
              <Lock className="h-3.5 w-3.5" /> {isCrypto ? "Crypto powered by Stripe · Settled in USD" : "Secured by Stripe · USD only"}
            </div>
            <div className="mt-4 flex items-start gap-2 rounded-lg bg-[#F3F4F6] p-3 text-xs text-ff-dark">
              <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-ff-green" />
              You'll receive an email receipt. FightFund reviews payout requests before funds are released.
            </div>
            <p className="mt-3 text-center text-[11px] leading-relaxed text-ff-medium" data-testid="donation-fee-disclosure">
              Funds raised are subject to a 5% platform fee (3% for Pro organizers) + Stripe processing fee. No hidden fees.
            </p>
          </div>
        </aside>
      </Container>

      <EmbeddedCheckoutModal
        open={!!clientSecret}
        onClose={() => { setClientSecret(""); setSubmitting(false); }}
        clientSecret={clientSecret}
        publishableKey={stripePk}
        title={recurring ? "Complete your monthly donation" : "Complete your donation"}
      />
    </div>
  );
}

function Row({ label, value, bold }) {
  return (
    <div className="flex items-center justify-between">
      <span className={bold ? "font-bold text-ff-blue" : "text-ff-dark"}>{label}</span>
      <span className={bold ? "font-heading text-lg font-extrabold text-ff-blue" : "font-semibold text-ff-blue"}>{value}</span>
    </div>
  );
}
